import React, { useMemo, useState, useRef } from "react";

// Hoisted helpers (module scope)
const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
const keyMatches = (candidate, key) => {
  const a = norm(candidate);
  const b = norm(key);
  return a === b || a.includes(b);
};
const isDealerObj = (x) =>
  x &&
  typeof x === "object" &&
  (Object.prototype.hasOwnProperty.call(x, "id") ||
    Object.prototype.hasOwnProperty.call(x, "name"));

const extractFromArrayBySubKey = (arr, subKey) => {
  // If it's already an array of dealers, return as-is
  if (arr.some(isDealerObj)) return arr;
  // Otherwise, try to pull from objects keyed by subcategory names
  const collected = [];
  for (const item of arr) {
    if (item && typeof item === "object") {
      const matchKey = Object.keys(item).find((k) => keyMatches(k, subKey));
      if (matchKey && Array.isArray(item[matchKey])) {
        collected.push(...item[matchKey]);
      }
    }
  }
  return collected;
};

const extractFromObjectByKey = (obj, key) => {
  // Try exact or includes match
  const direct =
    Object.keys(obj).find((k) => norm(k) === norm(key)) ||
    Object.keys(obj).find((k) => keyMatches(k, key));
  if (!direct) return undefined;
  const val = obj[direct];
  return val;
};

const filterDealersByText = (items, ...needles) => {
  const needle = norm(needles.filter(Boolean).join(" "));
  return items.filter((d) => norm(JSON.stringify(d)).includes(needle));
};

export default function MaterialCategory({ title, context, dealers = [] }) {
  const modules = useMemo(() => {
    if (!context) return [];
    return context
      .keys()
      .map((key) => {
        const mod = context(key);
        const Component = mod.default || mod;
        const name = key.replace("./", "").replace(/\.(js|jsx)$/, "");
        return { name, Component };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [context]);

  const prevTitleRef = useRef(title);
  const [activeName, setActiveName] = useState(modules[0]?.name || null);
  
  // Only reset activeName when the title (category) actually changes
  if (prevTitleRef.current !== title) {
    prevTitleRef.current = title;
    setActiveName(modules[0]?.name || null);
  }

  const ActiveComponent = modules.find((m) => m.name === activeName)?.Component;

  const resolvedSubDealers = useMemo(() => {
    const materialKey = String(title || "").trim();
    const subKey = String(activeName || "").trim();

    // 1) If top-level dealers is already an array of dealers, it's the pool for all subcategories
    if (Array.isArray(dealers)) {
      // Try to filter by subKey if possible; otherwise return as-is
      const maybeFiltered = isDealerObj(dealers[0])
        ? filterDealersByText(dealers, subKey) // best-effort filter if metadata exists
        : dealers;
      return maybeFiltered.length ? maybeFiltered : dealers;
    }

    // 2) If dealers is an object ... several shapes supported
    if (dealers && typeof dealers === "object") {
      // 2a) Try category key first
      const catVal = extractFromObjectByKey(dealers, materialKey);
      if (catVal !== undefined) {
        // Category value can be an array (dealers or array of subcategory objects) or an object keyed by subcategory
        if (Array.isArray(catVal)) {
          // If it's array of dealers -> return filtered by subKey if possible (best effort), else as-is
          // If it's array containing subcategory maps -> extract
          const fromArray = extractFromArrayBySubKey(catVal, subKey);
          if (fromArray.length) return fromArray;
          // Best-effort filter on dealers array for subKey
          const maybeFiltered = isDealerObj(catVal[0])
            ? filterDealersByText(catVal, subKey)
            : [];
          return maybeFiltered.length ? maybeFiltered : catVal;
        }
        if (catVal && typeof catVal === "object") {
          // Object keyed by subcategory
          const subVal = extractFromObjectByKey(catVal, subKey);
          if (Array.isArray(subVal)) return subVal;
          // If there's no direct array, try to flatten plausible arrays inside
          const flat = Object.values(catVal).flatMap((v) => (Array.isArray(v) ? v : []));
          if (flat.length) return flat;
        }
      }

      // 2b) If top-level object is id->dealer or mixed, build values and filter by material + subcategory
      const values = Object.entries(dealers).map(([id, v]) =>
        typeof v === "object" && v !== null ? { id, ...v } : { id, value: v }
      );
      const filtered = filterDealersByText(values, materialKey, subKey);
      if (filtered.length) return filtered;

      // 2c) Fallback: any arrays we can find inside the object
      const arraysInside = Object.values(dealers).flatMap((v) => (Array.isArray(v) ? v : []));
      if (arraysInside.length) return arraysInside;
    }

    // Default: none
    return [];
  }, [dealers, title, activeName]);

  return (
    <div className="p-6 h-full flex gap-4">
      <aside className="w-56 shrink-0 bg-gray-200 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700 flex flex-col max-h-full">
        <div className="p-3 font-semibold shrink-0 text-gray-900 dark:text-gray-100">{title}</div>
        <ul className="divide-y divide-gray-300 dark:divide-gray-700 overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-500 dark:[&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
          {modules.length === 0 && (
            <li className="p-3 text-sm text-gray-500 dark:text-gray-400">No modules found</li>
          )}
          {modules.map(({ name }) => (
            <li key={name}>
              <button
                onClick={() => setActiveName(name)}
                title={name}
                className={`w-full text-left p-3 hover:bg-gray-100 dark:hover:bg-gray-700 overflow-hidden ${
                  activeName === name ? "bg-white dark:bg-gray-900 font-medium text-gray-900 dark:text-gray-100" : "text-gray-900 dark:text-gray-100"
                }`}
              >
                <span className="block truncate">{name}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="flex-1 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700 overflow-auto">
        {ActiveComponent ? (
          <ActiveComponent dealers={resolvedSubDealers} />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
            Select a module
          </div>
        )}
      </section>
    </div>
  );
}
